import { ClinicalResultDataPointModel, HealthScoreResponseModel, ObservationGroupType } from 'src/app/api/proxy/datapoints/datapoints-models';
import { RangeLevel } from 'src/app/api/proxy/datapoints/datapoints-models';
import { GroupData } from 'src/app/controls/viva-score-guage/viva-score-guage.component';
import { TranslatorService } from 'src/app/services/translator-service/translator.service';
import { getObservationGroupDisplayName } from 'src/app/shared/health-score/observation-group-display.util';

type GroupNameTranslator = Pick<TranslatorService, 'translateText'>;

export function getHealthResultsLevelText(translator: TranslatorService, level?: RangeLevel | null): string {
  if (level == null || level === undefined) {
    return '';
  }
  switch (level) {
    case RangeLevel.Poor:
      return translator.translateText('health-results-component.level-poor');
    case RangeLevel.Fair:
      return translator.translateText('health-results-component.level-fair');
    case RangeLevel.Average:
      return translator.translateText('health-results-component.level-average');
    case RangeLevel.Good:
      return translator.translateText('health-results-component.level-good');
    case RangeLevel.Excellent:
      return translator.translateText('health-results-component.level-excellent');
    default:
      return '';
  }
}

export function buildGroupsFromBiometricDataPoints(
  currentScore: HealthScoreResponseModel | null,
  translator?: GroupNameTranslator,
): GroupData[] {
  if (!currentScore?.biometricDataPoints) {
    return [];
  }
  const grouped = new Map<ObservationGroupType, ClinicalResultDataPointModel[]>();
  currentScore.biometricDataPoints.forEach((point) => {
    if (point.group) {
      if (!grouped.has(point.group)) {
        grouped.set(point.group, []);
      }
      grouped.get(point.group)!.push(point);
    }
  });
  const groups: GroupData[] = [];
  grouped.forEach((points, groupType) => {
    const scores = points.map((p) => p.score || 0).filter((s) => s > 0);
    const avgScore = scores.length > 0
      ? scores.reduce((sum, s) => sum + s, 0) / scores.length
      : 0;
    const confidences = points
      .map((p) => (p as { confidencePercentage?: number }).confidencePercentage)
      .filter((c): c is number => c !== null && c !== undefined);
    const avgConfidence = confidences.length > 0
      ? confidences.reduce((sum, c) => sum + c, 0) / confidences.length
      : 0;
    let status = 'unknown';
    if (avgScore >= 4) status = 'excellent';
    else if (avgScore >= 3) status = 'good';
    else if (avgScore >= 2) status = 'average';
    else if (avgScore >= 1) status = 'fair';
    else status = 'poor';
    groups.push({
      id: groupType,
      groupId: groupType,
      name: getObservationGroupDisplayName(groupType, (key) => translator?.translateText(key) ?? key),
      score: Math.round(avgScore * 20),
      status,
      confidencePercentage: Math.round(avgConfidence),
      groupColor: '#666666',
      hasData: points.length > 0 && avgScore > 0,
      missingDataPoints: [],
      topDrivers: points
        .filter((p) => p.contributingPoints != null)
        .sort((a, b) => (b.contributingPoints || 0) - (a.contributingPoints || 0))
        .slice(0, 3),
    });
  });
  return groups;
}

export function getGroupRangeLevel(
  currentScore: HealthScoreResponseModel | null,
  group: GroupData,
): RangeLevel | null {
  if (!currentScore?.biometricDataPoints) {
    return null;
  }
  const groupPoints = currentScore.biometricDataPoints.filter(
    (point) => point.group === group.id,
  );
  if (groupPoints.length === 0) {
    return null;
  }
  const rangeLevels = groupPoints
    .map((p) => p.rangeLevel)
    .filter((level): level is RangeLevel => level !== null && level !== undefined);
  if (rangeLevels.length === 0) {
    return null;
  }
  const levelCounts = new Map<RangeLevel, number>();
  rangeLevels.forEach((level) => {
    levelCounts.set(level, (levelCounts.get(level) || 0) + 1);
  });
  let maxCount = 0;
  let mostCommonLevel: RangeLevel | null = null;
  levelCounts.forEach((count, level) => {
    if (count > maxCount) {
      maxCount = count;
      mostCommonLevel = level;
    }
  });
  return mostCommonLevel || rangeLevels[0];
}
